import React, { useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import MapComponent from './components/MapComponent';
import MenuContainer from './components/containers/MenuContainer';
import HighlightedRouteContainer from './components/containers/HighlightedRouteContainer';
import FilterContainer from './components/containers/FilterContainer';
import useFetchData from './components/hooks/useFetchData';
import './App.css';

function App() {
  const mapRef = useRef(null);
  const [highlightedRoute, setHighlightedRoute] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false); // State to store admin status
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [selectedRatings, setSelectedRatings] = useState([1, 2, 3, 4, 5]);
  const [distressEnabled, setDistressEnabled] = useState(false);
  const [mapRefreshKey, setMapRefreshKey] = useState(0); // New state for refreshing the map
  const [pointA, setPointA] = useState(null);
  const [pointB, setPointB] = useState(null);
  const [isSelectingPoints, setIsSelectingPoints] = useState(false);

  const { groupedData, fetchData } = useFetchData();


  const checkAdminStatus = async (user) => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = await user.getIdToken();

      const response = await fetch(`${apiUrl}/isAdmin`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to check admin status');

      const data = await response.json();
      setIsAdmin(data.isAdmin);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };


  // Firebase Authentication setup
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
      setIsAuthChecked(true);
      checkAdminStatus(user);

      if (user) {
        fetchData(user.uid); // Fetch data when user is authenticated
      } else {
        fetchData(null); // Clear data on logout
        setHighlightedRoute(null); // Reset highlighted route on logout
      }
    });
    return () => unsubscribe();
  }, [fetchData]);

  const handleRatingChange = (ratings) => setSelectedRatings(ratings);
  const handleDistressToggle = (enabled) => setDistressEnabled(enabled);

  const toggleSelectionMode = () => {
    setIsSelectingPoints(!isSelectingPoints);
    setPointA(null);
    setPointB(null);
  };


  const updateHighlightedRoute = () => {
    if (highlightedRoute?.video) {
      const updatedRouteData = groupedData[highlightedRoute.video];
      if (updatedRouteData) {
        setHighlightedRoute({
          ...highlightedRoute,
          coordinates: updatedRouteData.items,
          averageRating: updatedRouteData.items.reduce((acc, item) => acc + item.cvatRating, 0) / updatedRouteData.items.length
        });
      }
    }
  };

  // Update highlighted route whenever groupedData changes
  useEffect(() => {
    if (highlightedRoute) {
      updateHighlightedRoute();
    }
  }, [groupedData]);

  if (!isAuthChecked) {
    return <p>Loading...</p>;
  }

  return (
    <div className="App">
      <MenuContainer
        mapRef={mapRef}
        setMapRefreshKey={setMapRefreshKey} // Pass down setMapRefreshKey to MenuContainer
        isAdmin={isAdmin}
        highlightedRoute={highlightedRoute}
        setHighlightedRoute={setHighlightedRoute}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        groupedData={groupedData}
        fetchData={(userID) => fetchData(userID, true)} // Force data refresh when needed
      />

      <div className="main-content">
        <div className="sidepanelContainer">
          <div className="filterContainer">
            <FilterContainer
              onRatingChange={handleRatingChange}
              onDistressToggle={handleDistressToggle}
            />
          </div>
          <div className="routeContainer">
            <HighlightedRouteContainer
              highlightedRoute={highlightedRoute}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              selectedRatings={selectedRatings}
            />
          </div>

          <div className="plotRouteContainer">
            <button className="plot-route-button" onClick={toggleSelectionMode}>
              {isSelectingPoints ? "Cancel Selection" : "Select Points to Plot Route"}
              <p style={{ fontSize: '0.8rem', color: '#White', marginTop: '4px' }}>
                Click on this button and select two points on a route to analyze stats between them.
              </p>
            </button>
          </div>


        </div>

        <div className="mapContainer">
          <MapComponent
            ref={mapRef}
            key={mapRefreshKey} // Use mapRefreshKey to re-render the component
            selectedRatings={selectedRatings}
            distressEnabled={distressEnabled}
            isAdmin={isAdmin}
            currentUser={currentUser}
            setMapRefreshKey={setMapRefreshKey}
            groupedData={groupedData}
            setHighlightedRoute={setHighlightedRoute}
            highlightedRoute={highlightedRoute}
            fetchData={(userID) => fetchData(userID, true)} // Ensure map updates after fetchData is called
            setPointA={setPointA}     // Add this
            setPointB={setPointB}     // Add this
            pointA={pointA}           // New prop
            pointB={pointB}           // New prop
            isSelectingPoints={isSelectingPoints}       // New prop
            setIsSelectingPoints={setIsSelectingPoints} // New prop
          />
        </div>
      </div>
    </div>
  );
}

export default App;
